import { objectsMatchProperties } from "@/Common/helpers/objectsTs";
import isEqual from "lodash-es/isEqual";
import uniqBy from "lodash-es/uniqBy";
import { createContext, Dispatch, ReactNode, useCallback, useContext, useMemo, useState } from "react";

export interface SelectionItemBase {
	_id: string;
	table: string;
}

export interface SelectionItem extends SelectionItemBase {
	payPeriodId: string;
	commissionCycleId: string;
}

export interface SelectionItemInput extends SelectionItemBase {
	payPeriodId?: string;
	commissionCycleId?: string;
}

interface SelectionContextValue {
	selectedItems: SelectionItem[];
	addToSelection: (item: SelectionItemInput, isSingleSelect: boolean) => void;
	removeFromSelection: (item: SelectionItemInput) => void;
	addManyToSelection: (items: SelectionItemInput[]) => void;
	removeManyFromSelection: (items: SelectionItemInput[]) => void;
	clearSelection: (filterObjects?: Partial<Omit<SelectionItemInput, "_id">>[]) => void;
	payPeriodId: string;
	setPayPeriodId: Dispatch<React.SetStateAction<string>>;
	commissionCycleId: string;
	setCommissionCycleId: Dispatch<React.SetStateAction<string>>;
}

const SelectionContext = createContext<SelectionContextValue | null>(null);

const uniqByIteratee = ({ table, _id, commissionCycleId, payPeriodId }: SelectionItem) =>
	[table, _id, commissionCycleId, payPeriodId].join("|");

const buildItem = (item: SelectionItemInput, commissionCycleId: string, payPeriodId: string): SelectionItem => ({
	commissionCycleId,
	payPeriodId,
	...item,
});

const buildItems = (items: SelectionItemInput[], commissionCycleId: string, payPeriodId: string): SelectionItem[] => {
	return items.map((item) => buildItem(item, commissionCycleId, payPeriodId));
};

type InventoryFilterProps = {
	children: ReactNode;
};

function SelectionProvider({ children }: Readonly<InventoryFilterProps>) {
	const [selectedItems, setSelectedItems] = useState<SelectionItem[]>([]);
	const [payPeriodId, setPayPeriodId] = useState("");
	const [commissionCycleId, setCommissionCycleId] = useState("");

	const clearSelection = useCallback(
		(filterObjects?: Partial<Omit<SelectionItemInput, "_id">>[]) => {
			if (!filterObjects) {
				setSelectedItems([]);
				return;
			}
			setSelectedItems((prev) =>
				prev.filter(
					(item) =>
						!filterObjects.some((obj) =>
							objectsMatchProperties(obj, item, [
								...(obj.table ? (["table"] as const) : []),
								...(obj.commissionCycleId ? (["commissionCycleId"] as const) : []),
								...(obj.payPeriodId ? (["payPeriodId"] as const) : []),
							])
						)
				)
			);
		},
		[setSelectedItems]
	);

	const addToSelection = useCallback(
		(item: SelectionItemInput, isSingleSelect: boolean) =>
			setSelectedItems((prev) => {
				const completeItem = buildItem(item, commissionCycleId, payPeriodId);
				let newSelection = [...prev, completeItem];
				if (isSingleSelect) {
					newSelection = [...prev.filter((element) => element.table !== completeItem.table), completeItem];
				}
				return uniqBy(newSelection, uniqByIteratee);
			}),
		[setSelectedItems, commissionCycleId, payPeriodId]
	);

	const removeFromSelection = useCallback(
		(item: SelectionItemInput) => {
			setSelectedItems((prev) =>
				prev.filter((selectedItem) => !isEqual(selectedItem, buildItem(item, commissionCycleId, payPeriodId)))
			);
		},
		[setSelectedItems, commissionCycleId, payPeriodId]
	);

	const addManyToSelection = useCallback(
		(items: SelectionItemInput[]) =>
			setSelectedItems((prev) => {
				return uniqBy([...prev, ...buildItems(items, commissionCycleId, payPeriodId)], uniqByIteratee);
			}),
		[setSelectedItems, commissionCycleId, payPeriodId]
	);

	const removeManyFromSelection = useCallback(
		(items: SelectionItemInput[]) => {
			setSelectedItems((prev) =>
				prev.filter((selectedItem) => !items.some((item) => isEqual(selectedItem, buildItem(item, commissionCycleId, payPeriodId))))
			);
		},
		[setSelectedItems, commissionCycleId, payPeriodId]
	);

	const value = useMemo(
		() => ({
			selectedItems,
			addToSelection,
			removeFromSelection,
			addManyToSelection,
			removeManyFromSelection,
			clearSelection,
			payPeriodId,
			setPayPeriodId,
			commissionCycleId,
			setCommissionCycleId,
		}),
		[selectedItems, addToSelection, removeFromSelection, clearSelection]
	);

	return <SelectionContext.Provider value={value}>{children}</SelectionContext.Provider>;
}

const useSelectionContext = () => {
	const context = useContext(SelectionContext);
	if (!context) {
		throw new Error("useSelectionContext must be used within a SelectionProvider");
	}
	return context;
};

export { SelectionProvider, useSelectionContext };
